import { RefreshCw } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { VisitCard } from './VisitCard';
import type { SortedVisitItem } from './hooks/useSortedVisits';
import type { Task } from '@/api/tasks';
import type { ClientIndicatorsResponse } from '@/api/visits';

interface VisitListProps {
  items: SortedVisitItem[];
  completedItems: SortedVisitItem[];
  isLoading?: boolean;
  isRefetching?: boolean;
  onItemClick: (item: SortedVisitItem) => void;
  activeVisitTargetId?: number;
  showClientTasks?: boolean;
  tasksByClientId?: Record<number, Task[]>;
  onTaskSelect?: (task: Task) => void;
  indicatorsData?: ClientIndicatorsResponse;
  clientsWithWaybills?: Set<string>;
  onLongPress?: (item: SortedVisitItem, pos: { x: number; y: number }) => void;
  highlightedItemId?: number;
}

function VisitListSkeleton() {
  return (
    <div className="space-y-2 p-4">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="p-3 rounded-lg border space-y-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
          <Skeleton className="h-3 w-1/4" />
        </div>
      ))}
    </div>
  );
}

export function VisitList({
  items,
  completedItems,
  isLoading,
  isRefetching,
  onItemClick,
  activeVisitTargetId,
  showClientTasks,
  tasksByClientId,
  onTaskSelect,
  indicatorsData,
  clientsWithWaybills,
  onLongPress,
  highlightedItemId,
}: VisitListProps) {
  if (isLoading) {
    return <VisitListSkeleton />;
  }

  if (items.length === 0 && completedItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-muted-foreground">Нет клиентов на этой неделе</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Refetching indicator */}
      {isRefetching && (
        <div className="absolute top-2 right-4 z-10">
          <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Pending visits */}
      <div className="p-4 space-y-2">
        {items.map((item) => (
          <VisitCard
            key={item.visitTargetId}
            item={item}
            onItemClick={onItemClick}
            isActive={item.visitTargetId === activeVisitTargetId}
            clientTasks={showClientTasks ? tasksByClientId?.[item.client.id] : undefined}
            onTaskSelect={onTaskSelect}
            indicators={indicatorsData?.indicators[item.client.id]}
            hasWaybills={clientsWithWaybills?.has(item.client.name)}
            onLongPress={onLongPress}
            isHighlighted={item.visitTargetId === highlightedItemId}
          />
        ))}
      </div>

      {/* Completed visits section */}
      {completedItems.length > 0 && (
        <div className="border-t">
          <div className="px-4 py-2 bg-muted/50">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Посещённые ({completedItems.length})
            </span>
          </div>
          <div className="p-4 space-y-2">
            {completedItems.map((item) => (
              <VisitCard
                key={item.visitTargetId}
                item={item}
                onItemClick={onItemClick}
                clientTasks={showClientTasks ? tasksByClientId?.[item.client.id] : undefined}
                onTaskSelect={onTaskSelect}
                indicators={indicatorsData?.indicators[item.client.id]}
                hasWaybills={clientsWithWaybills?.has(item.client.name)}
                onLongPress={onLongPress}
                isHighlighted={item.visitTargetId === highlightedItemId}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
